import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card, Button, Input, Badge } from "@/components/design-system";
import { TID } from "@/lib/testIds";

export default function Settings() {
  const { user, logout, addRole } = useAuth();
  const nav = useNavigate();
  const [confirmErase, setConfirmErase] = useState("");

  if (!user) return null;

  const runExport = async () => {
    const { data } = await api.get("/users/me/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `saaslaunchinglab-export-${user.email}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const runErase = async () => {
    if (confirmErase !== "DELETE") return;
    await api.delete("/users/me");
    await logout();
    nav("/", { replace: true });
  };

  const missingRoles = ["founder", "community"].filter((r) => !user.roles?.includes(r));

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Settings</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Account & data</h1>
        <p className="text-[#0B1F3A]/70 mt-2">Manage your identity and your GDPR data rights.</p>
      </div>

      {/* Profile */}
      <Card>
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Profile</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-[#0B1F3A]/60 text-xs uppercase tracking-widest">Name</div><div className="font-medium">{user.name}</div></div>
          <div><div className="text-[#0B1F3A]/60 text-xs uppercase tracking-widest">Email</div><div className="font-medium">{user.email} {user.email_verified ? <Badge variant="active">Verified</Badge> : <Badge variant="admin">Unverified</Badge>}</div></div>
          <div className="sm:col-span-2">
            <div className="text-[#0B1F3A]/60 text-xs uppercase tracking-widest mb-2">Roles</div>
            <div className="flex flex-wrap gap-2">
              {user.roles?.map((r) => <Badge key={r} variant={r === "admin" ? "admin" : r === "founder" ? "founder" : r === "ambassador" ? "ambassador" : "community"}>{r}</Badge>)}
            </div>
            {missingRoles.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {missingRoles.map((r) => (
                  <Button key={r} data-testid={`${TID.settings.addRole}-${r}`} variant="secondary" onClick={async () => { try { await addRole(r); } catch(_){} }}>
                    Also become a {r}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* GDPR */}
      <Card>
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Your data (GDPR)</h2>
        <p className="text-sm text-[#0B1F3A]/70 mt-1">You have the right to a copy of every piece of data we hold about you, and the right to have your account erased.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button data-testid={TID.settings.gdprExport} variant="secondary" onClick={runExport}>Download my data (JSON)</Button>
        </div>
        <div className="mt-8 pt-6 border-t border-[#0B1F3A]/10">
          <h3 className="font-[Montserrat] font-bold text-lg text-red-700">Delete my account</h3>
          <p className="text-sm text-[#0B1F3A]/70 mt-1">Type <b>DELETE</b> to confirm. This will permanently erase your account.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Input value={confirmErase} onChange={(e) => setConfirmErase(e.target.value)} placeholder="Type DELETE" className="max-w-xs" />
            <Button data-testid={TID.settings.gdprErase} variant="danger" onClick={runErase} disabled={confirmErase !== "DELETE"}>Erase my account</Button>
          </div>
        </div>
      </Card>

      <p className="text-xs text-[#0B1F3A]/50">
        Privacy / data-protection questions: <a href="mailto:info@pastoria.es" className="text-[#2D7FF9]">info@pastoria.es</a>. General help: <a href="mailto:info@saaslaunchinglab.com" className="text-[#2D7FF9]">info@saaslaunchinglab.com</a>. All personal data is stored in the EU.
      </p>
    </div>
  );
}
